"use client"

import { useTranslations } from "next-intl"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Robot,
  Gauge,
  TerminalWindow,
  Chat,
  Archive,
  Warning,
  CheckCircle,
  ArrowRight,
  User as UserIcon,
  SignOut,
  CreditCard,
} from "@phosphor-icons/react/dist/ssr"
import { useAuth } from "@/hooks/use-auth"
import { useSubscription } from "@/hooks/use-subscription"
import { PricingPlans } from "@/components/features/pricing-plans"

export default function DashboardPage({ params }: { params: { locale: string } }) {
  const { locale } = params
  const t = useTranslations("dashboard")
  const { user, loading, logout } = useAuth()
  const { subscription, loading: subLoading } = useSubscription()
  const isAdmin = user?.role === "ADMIN"

  if (loading || (!isAdmin && subLoading)) return null

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in px-4 py-6 md:px-6 md:py-8">
      {/* --- UserHeader (always visible: name + logout) --- */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <UserIcon weight="regular" className="text-xl text-muted" aria-hidden="true" />
          <div>
            <p className="font-semibold text-on-dark">{user?.display_name}</p>
            <p className="text-xs text-muted">@{user?.id.slice(0, 8)}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout}>
          <SignOut weight="regular" className="mr-2" aria-hidden="true" />
          {t("logout")}
        </Button>
      </div>

      {isAdmin && (
        <>
          {/* --- GraceBanner --- */}
          <div className="mb-6 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/10 px-5 py-3">
            <div className="flex items-center gap-3">
              <Warning weight="fill" className="text-lg text-primary" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-primary">{t("grace_title")}</p>
                <p className="text-xs text-muted">
                  {t("grace_desc", { days: 3 })}{" "}
                  <a href={`/${locale}/subscriptions`} className="text-primary underline">{t("grace_renew")}</a>
                </p>
              </div>
            </div>
            <a href={`/${locale}/subscriptions`}>
              <Button variant="primary" size="sm">
                {t("grace_cta")}
              </Button>
            </a>
          </div>

          {/* --- SectionTitle --- */}
          <div className="mb-8">
            <h1 className="text-display-sm text-on-dark">{t("title")}</h1>
            <p className="mt-2 text-muted">{t("sub")}</p>
          </div>

          {/* --- AdminContent (stats + cards + quick actions) --- */}
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: t("stat_agent_status"), value: t("stat_value_running"), icon: <Robot weight="regular" />, color: "text-trading-up" },
              { label: t("stat_credits"), value: "12,400", icon: <Gauge weight="regular" />, color: "text-primary", sub: "of 50,000" },
              { label: t("stat_telegram"), value: t("stat_value_connected"), icon: <Chat weight="regular" />, color: "text-trading-up" },
              { label: t("stat_backup"), value: "2h ago", icon: <Archive weight="regular" />, color: "text-muted" },
            ].map((s) => (
              <Card key={s.label} variant="interactive">
                <CardContent className="p-6">
                  <div className="mb-3 text-2xl text-muted" aria-hidden="true">{s.icon}</div>
                  <p className={`font-number text-display-sm ${s.color}`}>{s.value}</p>
                  <p className="text-sm text-muted">{s.label}</p>
                  {s.sub && <p className="text-xs text-muted">{s.sub}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Robot weight="regular" className="text-2xl text-primary" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold text-on-dark">{t("card_agent_title")}</h3>
                    <p className="text-sm text-muted">claweb v2.4.1 · {t("card_agent_sub")}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted">{t("card_agent_status")}</span>
                    <p className="flex items-center gap-1 font-medium text-trading-up">
                      <CheckCircle weight="fill" className="text-xs" aria-hidden="true" /> {t("stat_value_running")}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted">{t("card_agent_uptime")}</span>
                    <p className="font-number font-medium text-on-dark">3d 12h</p>
                  </div>
                  <div>
                    <span className="text-muted">{t("card_agent_cpu")}</span>
                    <p className="font-number font-medium text-on-dark">23%</p>
                  </div>
                  <div>
                    <span className="text-muted">{t("card_agent_memory")}</span>
                    <p className="font-number font-medium text-on-dark">1.2 GB</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="secondary" size="sm">{t("card_agent_restart")}</Button>
                  <Button variant="ghost" size="sm">{t("card_agent_logs")}</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Gauge weight="regular" className="text-2xl text-primary" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold text-on-dark">{t("card_usage_title")}</h3>
                    <p className="text-sm text-muted">{t("card_usage_sub")}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  {[
                    { label: t("usage_credits_included"), value: "50,000" },
                    { label: t("usage_credits_used"), value: "12,400" },
                    { label: t("usage_additional"), value: "2,400" },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center justify-between">
                      <span className="text-muted">{r.label}</span>
                      <span className="font-number text-on-dark">{r.value}</span>
                    </div>
                  ))}
                  <div className="border-t border-hairline-dark pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted">{t("usage_additional_charge")}</span>
                      <span className="font-number font-semibold text-primary">$4.80</span>
                    </div>
                  </div>
                </div>
                <a href={`/${locale}/ai-usage`} className="inline-block mt-3">
                  <Button variant="ghost" size="sm">
                    {t("usage_full_report")} <ArrowRight weight="regular" aria-hidden="true" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { label: t("quick_terminal"), desc: t("quick_terminal_desc"), href: "/terminal", icon: <TerminalWindow weight="regular" /> },
              { label: t("quick_telegram"), desc: t("quick_telegram_desc"), href: "/telegram", icon: <Chat weight="regular" /> },
              { label: t("quick_backups"), desc: t("quick_backups_desc"), href: "/backups", icon: <Archive weight="regular" /> },
            ].map((q) => (
              <a key={q.label} href={`/${locale}${q.href}`}>
                <Card variant="interactive" className="h-full">
                  <CardContent className="p-6">
                    <div className="mb-3 text-2xl text-primary" aria-hidden="true">{q.icon}</div>
                    <h4 className="font-semibold text-on-dark">{q.label}</h4>
                    <p className="text-sm text-muted">{q.desc}</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </>
      )}

      {!isAdmin && (
        <>
          {/* --- SectionTitle --- */}
          <div className="mb-8">
            <h1 className="text-display-sm text-on-dark">{t("title")}</h1>
            <p className="mt-2 text-muted">{t("sub")}</p>
          </div>

          {subscription ? (
            /* --- ActiveSubscription card --- */
            <Card className="max-w-md">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <CreditCard weight="regular" className="text-2xl text-primary" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold text-on-dark">
                      {t("current_plan_label")}: {subscription.plan_code}
                    </h3>
                    <p className="text-sm text-trading-up">{t("status_active")}</p>
                  </div>
                </div>
                <a href={`/${locale}/subscriptions`}>
                  <Button variant="primary" size="sm">
                    {t("manage_subscription")} <ArrowRight weight="regular" aria-hidden="true" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : (
            /* --- NoSubscription: prompt to pick a plan --- */
            <>
              <div className="mb-8 max-w-2xl">
                <div className="mb-2 flex items-center gap-2">
                  <Robot weight="fill" className="text-lg text-primary" aria-hidden="true" />
                  <h2 className="text-xl font-semibold text-on-dark">{t("no_agent_title")}</h2>
                </div>
                <p className="text-muted">{t("no_agent_desc")}</p>
              </div>
              <PricingPlans ctaHref={`/${locale}/subscriptions/checkout`} showHeader={false} />
            </>
          )}
        </>
      )}
    </div>
  )
}
